import { useObserva } from '@morganisbatman/observa-client-react'
import { useCallback, useEffect } from 'react'
import {
  CHECKOUT_CLICKED,
  CHECKOUT_FAILED,
  CONSENT_TOGGLED,
  CTA_VIEWED,
  RESULT_VIEWED,
  checkoutClickedPayload,
  checkoutFailedPayload,
  consentToggledPayload,
  ctaViewedPayload,
  resultViewedPayload,
} from '../lib/paywall-events'

type Jurisdiction = 'ENGLAND' | 'WALES' | 'SCOTLAND'

// Fires app.result.viewed once when a verdict screen renders — the in-app
// "arrived from the marketing handoff and saw the verdict" denominator. Emits
// are fire-and-forget (telemetry never blocks the UI), matching useScreenTracking.
export const useResultViewed = (
  bandCheckId: string,
  flagged: boolean,
  jurisdiction: Jurisdiction,
) => {
  const observa = useObserva()
  useEffect(() => {
    void observa
      .event({
        eventType: RESULT_VIEWED,
        payload: resultViewedPayload(bandCheckId, flagged, jurisdiction),
      })
      .catch(() => {})
  }, [observa, bandCheckId, flagged, jurisdiction])
}

// Instrumentation for the £4.99 paywall (PaidAppealCTA). Fires appeal.cta.viewed
// on mount — the paywall only renders for paid-eligible verdicts, so mounting
// the component IS "saw the offer" — and returns bound emitters for the three
// user actions that follow. Extracting this keeps PaidAppealCTA focused on UI;
// every payload carries bandCheckId for the cross-origin join.
export const usePaywallTracking = (bandCheckId: string) => {
  const observa = useObserva()
  const emit = useCallback(
    (eventType: string, payload: Record<string, unknown>) => {
      void observa.event({ eventType, payload }).catch(() => {})
    },
    [observa],
  )
  useEffect(() => {
    emit(CTA_VIEWED, ctaViewedPayload(bandCheckId))
  }, [emit, bandCheckId])
  return {
    onConsentToggle: (checked: boolean) =>
      emit(CONSENT_TOGGLED, consentToggledPayload(bandCheckId, checked)),
    onCheckoutClick: (hasPromo: boolean) =>
      emit(CHECKOUT_CLICKED, checkoutClickedPayload(bandCheckId, hasPromo)),
    onCheckoutFailed: (reason: string) =>
      emit(CHECKOUT_FAILED, checkoutFailedPayload(bandCheckId, reason)),
  }
}
